import { api } from "./api.js";
import { esc } from "./utils.js";

/**
 * A type-to-search replacement for a <select> fed by a list endpoint.
 *
 * The selects it replaces loaded every option up front, which stopped
 * working once the list endpoints began paginating: past the first page
 * the remaining options simply were not there, with nothing on screen to
 * say so. Searching asks the server for matches instead, so the number
 * of dive sites stops being a limit on what you can pick.
 *
 * The chosen id lives in a hidden input, so submitting code reads it the
 * same way it read the old select's value. An empty box means "none",
 * which is what the old "None" option meant.
 */

const DEBOUNCE_MS = 250;
const RESULT_LIMIT = 10;

export function searchPickerHtml(
  id,
  { label, placeholder = "Type to search", value = "", valueLabel = "", hint = "" } = {}
) {
  return `
    <label class="form-label" for="${esc(id)}-input">${esc(label)}</label>
    <div class="position-relative" data-picker="${esc(id)}">
      <input type="text" class="form-control" id="${esc(id)}-input"
             autocomplete="off" role="combobox" aria-expanded="false"
             aria-autocomplete="list" aria-controls="${esc(id)}-list"
             placeholder="${esc(placeholder)}" value="${esc(valueLabel)}" />
      <!-- What the form actually submits. The visible field is a label. -->
      <input type="hidden" id="${esc(id)}" value="${esc(value)}" />
      <ul class="list-group position-absolute w-100 shadow-sm d-none" id="${esc(id)}-list"
          role="listbox" style="z-index:5;max-height:240px;overflow-y:auto;"></ul>
      ${hint ? `<div class="form-text">${esc(hint)}</div>` : ""}
    </div>`;
}

/** The chosen id, or null for "none". */
export function pickerValue(container, id) {
  const raw = container.querySelector(`#${id}`)?.value ?? "";
  return raw ? Number(raw) : null;
}

/**
 * Wires a picker up.
 *
 * `path` is the list endpoint (it must support ?q=), and `detailPath`
 * builds the URL for one record — needed when the form arrives with an
 * id but no name, as create-post does when opened from a dive event.
 *
 * `query` is merged into every search. The cohort pickers use it to ask
 * for only the reader's own cohorts: the API refuses to tag content with
 * a cohort you are not in, so offering the rest is offering a rejection.
 * It is deliberately not applied to `detailPath` — a post tagged before
 * its author left the cohort must still show what it is tagged with.
 */
export function attachSearchPicker(container, {
  id,
  path,
  query = {},
  detailPath = (value) => `${path}/${value}`,
  labelOf = (item) => item.name,
  onChange = null,
}) {
  const root = container.querySelector(`[data-picker="${id}"]`);
  if (!root) return;
  const input = root.querySelector(`#${id}-input`);
  const hidden = root.querySelector(`#${id}`);
  const list = root.querySelector(`#${id}-list`);

  let debounce;
  let results = [];
  let highlighted = -1;
  // Rises with every search so a slow earlier response cannot overwrite
  // the results of a later one.
  let generation = 0;

  function close() {
    list.classList.add("d-none");
    list.innerHTML = "";
    results = [];
    highlighted = -1;
    input.setAttribute("aria-expanded", "false");
  }

  function choose(item) {
    hidden.value = item == null ? "" : String(item.id);
    input.value = item == null ? "" : labelOf(item);
    close();
    if (onChange) onChange(item);
  }

  function paint() {
    if (!results.length) {
      list.innerHTML = `<li class="list-group-item text-slate small">No matches</li>`;
    } else {
      list.innerHTML = results
        .map(
          (item, i) => `
          <li class="list-group-item list-group-item-action ${i === highlighted ? "active" : ""}"
              role="option" aria-selected="${i === highlighted}" data-index="${i}"
              style="cursor:pointer;">${esc(labelOf(item))}</li>`
        )
        .join("");
      list.querySelectorAll("[data-index]").forEach((el) => {
        // mousedown, not click: blur fires first on a click and would
        // close the list out from under the pointer.
        el.addEventListener("mousedown", (e) => {
          e.preventDefault();
          choose(results[Number(el.dataset.index)]);
        });
      });
    }
    list.classList.remove("d-none");
    input.setAttribute("aria-expanded", "true");
  }

  async function search(term) {
    const mine = ++generation;
    try {
      const { items } = await api.getPage(path, {
        query: { ...query, q: term },
        limit: RESULT_LIMIT,
      });
      if (mine !== generation) return; // a newer search already answered
      results = items;
      highlighted = -1;
      paint();
    } catch (err) {
      if (mine !== generation) return;
      results = [];
      list.innerHTML = `<li class="list-group-item text-danger small">Could not search</li>`;
      list.classList.remove("d-none");
    }
  }

  input.addEventListener("input", () => {
    // Typing after choosing means the choice no longer matches what is
    // on screen, so it is cleared rather than left silently attached.
    hidden.value = "";
    if (onChange) onChange(null);

    const term = input.value.trim();
    clearTimeout(debounce);
    if (!term) {
      close();
      return;
    }
    debounce = setTimeout(() => search(term), DEBOUNCE_MS);
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      close();
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      highlighted = (highlighted + step + results.length) % results.length;
      paint();
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      choose(results[highlighted]);
    }
  });

  // A delay, because blur beats the list's own mousedown on some
  // browsers and the choice would be lost.
  input.addEventListener("blur", () => setTimeout(close, 150));

  /** Fills in the label for an id the form already had. */
  async function resolveInitial() {
    if (!hidden.value || input.value) return;
    try {
      const item = await api.get(detailPath(hidden.value));
      input.value = labelOf(item);
    } catch (err) {
      // The id is still submitted; only its name is missing, and an
      // empty box would look like nothing was selected.
      input.value = `#${hidden.value}`;
    }
  }

  /** Chooses item ({id, name}) as if the reader had picked it. */
  return { resolveInitial, choose };
}
